import { useEffect, useState } from 'react'
import type { ServerApiClient } from '../../../data/ServerApiClient'

/**
 * C5 — the close-status banner. A small, calm strip near the top of the Ratios
 * (and Financials) pages that surfaces the as-of boundary when the Client
 * Understanding ledger has diagnosed a reconciled period.
 *
 * The belief arrives as a `forecast.reconciled_through` claim whose
 * `directive_json` is `{ kind: "reconciled_through", period: "<YYYY-MM>" }`.
 * When no such belief is known (or the fetch fails) this renders nothing — the
 * banner is purely additive, so a no-belief workbook is visually unchanged.
 */
export type CfoClosePeriodBannerProps = {
  appApi: ServerApiClient
  workbookId: string | null
}

const RECONCILED_THROUGH_KIND = 'forecast.reconciled_through'

/** Format a `YYYY-MM` (or `YYYY-MM-DD`) period as e.g. "May 2026". Returns null on garbage. */
const formatClosePeriod = (period: unknown): string | null => {
  if (typeof period !== 'string') return null
  const trimmed = period.trim()
  const match = /^(\d{4})-(\d{2})/.exec(trimmed)
  if (!match) return null
  const year = Number(match[1])
  const month = Number(match[2])
  if (!Number.isFinite(year) || month < 1 || month > 12) return null
  const parsed = new Date(Date.UTC(year, month - 1, 1))
  if (Number.isNaN(parsed.getTime())) return null
  return parsed.toLocaleDateString(undefined, { month: 'long', year: 'numeric', timeZone: 'UTC' })
}

/** Pull the reconciled-through period string out of a raw claim's `directive_json`. */
const reconciledPeriodFromDirective = (directiveJson: unknown): string | null => {
  if (!directiveJson || typeof directiveJson !== 'object') return null
  const period = (directiveJson as { period?: unknown }).period
  return typeof period === 'string' ? period : null
}

export function CfoClosePeriodBanner({ appApi, workbookId }: CfoClosePeriodBannerProps) {
  const [closeLabel, setCloseLabel] = useState<string | null>(null)

  useEffect(() => {
    if (!workbookId) {
      setCloseLabel(null)
      return
    }
    let cancelled = false
    void (async () => {
      try {
        const getBootstrap = (appApi as Partial<ServerApiClient>).getAccountingWorkbookBootstrap
        const provider = typeof getBootstrap === 'function'
          ? (await getBootstrap.call(appApi, workbookId)).accounting_provider
          : 'quickbooks'
        let period: string | null = null
        if (provider === 'quickbooks') {
          const response = await appApi.getQuickBooksUnderstanding(workbookId)
          const claim = (response?.claims || []).find(
            (entry) => entry?.directive_kind === RECONCILED_THROUGH_KIND,
          )
          period = claim ? reconciledPeriodFromDirective(claim.directive_json) : null
        } else {
          const close = await appApi.getAccountingClose(workbookId)
          period = typeof close?.periods?.[0]?.period === 'string' ? close.periods[0].period : null
        }
        const label = formatClosePeriod(period)
        if (!cancelled) setCloseLabel(label)
      } catch {
        // Swallow — no belief, no banner. The page is unaffected.
        if (!cancelled) setCloseLabel(null)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [appApi, workbookId])

  if (!closeLabel) return null

  return (
    <div
      className="cfo-shell-notice"
      role="status"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginBottom: 'var(--s3)',
        fontSize: 13,
        color: 'var(--muted)',
      }}
    >
      <span aria-hidden="true" style={{ fontSize: 14, lineHeight: 1 }}>
        ✓
      </span>
      <span>
        Showing <strong style={{ color: 'var(--ink)' }}>{closeLabel}</strong> — your last closed month
      </span>
    </div>
  )
}

export default CfoClosePeriodBanner
